import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment } from "vue"

import type { CSSProperties } from 'vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

interface Props {
  message: string;
  size?: 'large' | 'medium' | 'small';
  position?: 'top' | 'bottom' | 'left' | 'right';
  allowMultiLine?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomTooltip',
  props: {
    message: {},
    size: { default: 'medium' },
    position: { default: 'top' },
    allowMultiLine: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const triggerEl = ref<HTMLElement | null>(null);
const tooltipEl = ref<HTMLElement | null>(null);

const visible = ref(false);
const finalPosition = ref(props.position);
const tooltipStyle = ref<CSSProperties>({
  position: 'fixed',
  top: '0px',
  left: '0px',
  transform: 'translate(-50%, -100%)',
  zIndex: '9999'
});
const arrowOffsetX = ref(0);
const arrowOffsetY = ref(0);
const arrowVarStyle = ref<CSSProperties>({});

function onMouseEnter() {
  showTooltip();
}
function onFocusIn() {
  showTooltip();
}
function onMouseLeave() {
  hideTooltip();
}
function onFocusOut() {
  hideTooltip();
}
function showTooltip() {
  visible.value = true;
  nextTick(() => positionTooltip());
}
function hideTooltip() {
  visible.value = false;
}

/**
 * Calculates the position of the tooltip and performs:
 *  - Vertical flip (top<->bottom) if it doesn't fit.
 *  - Horizontal correction if it exceeds the side edges.
 *  - Adjustment of the arrow to keep pointing to the element.
 */
function positionTooltip() {
  const trigger = triggerEl.value;
  const tooltip = tooltipEl.value;
  if (!trigger || !tooltip) return;

  const rect = trigger.getBoundingClientRect();
  const tooltipWidth = tooltip.offsetWidth;
  const tooltipHeight = tooltip.offsetHeight;
  finalPosition.value = props.position;

  const offset = 8;
  let top = 0;
  let left = 0;

  switch (finalPosition.value) {
    case 'top':
      top = rect.top - offset;
      left = rect.left + rect.width / 2;
      break;
    case 'bottom':
      top = rect.bottom + offset;
      left = rect.left + rect.width / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - offset;
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + offset;
      break;
  }

  if (finalPosition.value === 'top') {
    const topEdge = top - tooltipHeight;
    if (topEdge < 0) {
      finalPosition.value = 'bottom';
      top = rect.bottom + offset;
    }
  } else if (finalPosition.value === 'bottom') {
    const bottomEdge = top + tooltipHeight;
    if (bottomEdge > window.innerHeight) {
      finalPosition.value = 'top';
      top = rect.top - offset;
    }
  }

  let transform = '';
  switch (finalPosition.value) {
    case 'top':
      transform = 'translate(-50%, -100%)';
      break;
    case 'bottom':
      transform = 'translate(-50%, 0)';
      break;
    case 'left':
      transform = 'translate(-100%, -50%)';
      break;
    case 'right':
      transform = 'translate(0, -50%)';
      break;
  }

  arrowOffsetX.value = 0;
  arrowOffsetY.value = 0;

  if (finalPosition.value === 'top' || finalPosition.value === 'bottom') {
    const halfWidth = tooltipWidth / 2;
    const leftEdge = left - halfWidth;
    const rightEdge = left + halfWidth;

    if (leftEdge < 0) {
      const shift = -leftEdge;
      left += shift;
      arrowOffsetX.value = -shift;
    } else if (rightEdge > window.innerWidth) {
      const shift = window.innerWidth - rightEdge;
      left += shift;
      arrowOffsetX.value = -shift;
    }
  }

  tooltipStyle.value.top = `${top}px`;
  tooltipStyle.value.left = `${left}px`;
  tooltipStyle.value.transform = transform;

  arrowVarStyle.value = {
    '--arrow-offset-x': arrowOffsetX.value + 'px',
    '--arrow-offset-y': arrowOffsetY.value + 'px'
  };
}

function onScrollOrResize() {
  if (visible.value) {
    positionTooltip();
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize, true);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      ref_key: "triggerEl",
      ref: triggerEl,
      class: "trigger-wrapper",
      onMouseenter: onMouseEnter,
      onMouseleave: onMouseLeave,
      onFocusin: onFocusIn,
      onFocusout: onFocusOut
    }, [
      _renderSlot(_ctx.$slots, "default")
    ], 544),
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (visible.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "tooltipEl",
              ref: tooltipEl,
              class: _normalizeClass(["tooltiptext", [finalPosition.value, _ctx.size, { multiline: _ctx.allowMultiLine }]]),
              style: _normalizeStyle([tooltipStyle.value, arrowVarStyle.value])
            }, _toDisplayString(_ctx.message), 7))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})